/**
 * @fileoverview Document title + meta description hook.
 * @module hooks/useDocumentTitle
 * @description
 * Replaces the Angular router `data.title` / `data.description` mechanism. Sets
 * `document.title` and the `<meta name="description">` content from the passed
 * args, updating whenever they change. When an arg is undefined, the value falls
 * back to the default already present in `index.html` (captured on first run).
 *
 * @author Liraz Amir
 */

import { useEffect } from 'react';

/** Default title baked into index.html, captured before any override. */
const defaultTitle = typeof document !== 'undefined' ? document.title : '';

/** Read the current meta-description content (the index.html default). */
function getMetaDescriptionElement(): HTMLMetaElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  return document.querySelector('meta[name="description"]');
}

const defaultDescription = getMetaDescriptionElement()?.content ?? '';

/**
 * Set the document title and meta description. Undefined args fall back to the
 * index.html defaults.
 */
export function useDocumentTitle(title?: string, description?: string): void {
  useEffect(() => {
    document.title = title ?? defaultTitle;

    const meta = getMetaDescriptionElement();
    if (meta) {
      meta.content = description ?? defaultDescription;
    }
  }, [title, description]);
}

export default useDocumentTitle;
